import React from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { DialogField, DialogFieldLabel, SegmentedControl } from '@aibuild/ui';
import type { AppRunsOn } from '@api/apps';
import { FieldHint } from './AppAgentFields.styled';

interface AppRunsOnFieldProps {
  readonly value: AppRunsOn;
  readonly onChange: (runsOn: AppRunsOn) => void;
}

const hintFor = (runsOn: AppRunsOn, intl: IntlShape): string => {
  switch (runsOn) {
    case 'CLOUD':
      return intl.formatMessage({
        id: 'apps.builder.details.runsOn.cloud.hint',
        defaultMessage: 'Runs in the Aibuild cloud. Skills marked Local only are not available.',
      });
    case 'LOCAL':
      return intl.formatMessage({
        id: 'apps.builder.details.runsOn.local.hint',
        defaultMessage:
          "Runs on the local runner of whoever runs the app, so it can use software installed there, such as Fusion or Cura.",
      });
    case 'CHOICE':
      return intl.formatMessage({
        id: 'apps.builder.details.runsOn.choice.hint',
        defaultMessage:
          'The person running picks the cloud or their own runner, so only skills that work on both can be used.',
      });
    default: {
      const unhandled: never = runsOn;
      throw new Error(`Unhandled runsOn: ${String(unhandled)}`);
    }
  }
};

const AppRunsOnField: React.FC<AppRunsOnFieldProps> = ({ value, onChange }) => {
  const intl = useIntl();
  const label = intl.formatMessage({ id: 'apps.builder.details.runsOn', defaultMessage: 'Runs on' });
  return (
    <DialogField>
      <DialogFieldLabel as="span">{label}</DialogFieldLabel>
      <SegmentedControl<AppRunsOn>
        options={[
          { id: 'CLOUD', label: intl.formatMessage({ id: 'apps.builder.details.runsOn.cloud', defaultMessage: 'Cloud' }) },
          {
            id: 'LOCAL',
            label: intl.formatMessage({ id: 'apps.builder.details.runsOn.local', defaultMessage: 'Their computer' }),
          },
          {
            id: 'CHOICE',
            label: intl.formatMessage({ id: 'apps.builder.details.runsOn.choice', defaultMessage: 'Their choice' }),
          },
        ]}
        value={value}
        onChange={onChange}
        ariaLabel={label}
        dataTestId="app-builder__runs-on"
      />
      <FieldHint>{hintFor(value, intl)}</FieldHint>
    </DialogField>
  );
};

export default AppRunsOnField;
